import { useState, useEffect } from "react";
import Heading from "../heading/Heading";
import DirectoryItem from "./directoryItem/DirectoryItem";
import List from "@mui/material/List";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import { selectDirectoryOptions } from "./directorySlice";
import { selectLockedFilter } from "../searchPanel/searchSlice";
import { getTotals } from "../../../services";
import { getDatasetId } from "../../../utils/window-utils";
import { selectCustomMarkers } from "../../../app/configSlice";
import { getDirectoryIconSrc } from "./getDirectoryIconSrc";

const StyledDirectoryPanel = styled(Box)(() => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  padding: "var(--spacing-medium) 0 80px",
  maxWidth: "var(--panel-width-desktop)",
  margin: "0 auto",
  "@media (min-width: 768px)": {
    padding: "var(--spacing-large) 0",
  },
}));

const fetchResults = async (lockedFilter: string[]) => {
  const datasetId = getDatasetId();
  if (datasetId == null) {
    return { failed: true };
  }

  const response = await getTotals({
    params: { datasetId },
    query: {
      filter: lockedFilter.length > 0 ? lockedFilter : undefined,
    },
  });

  switch (response.status) {
    case 200: {
      const body = response.body;
      return { resultsTotals: body };
    }

    case 400:
      throw new Error("400: Bad request " + response.body.message);
    case 404:
      throw new Error("404: Endpoint not found " + response.body.message);
  }
};

const isAnyOption = (value: string) => value === "any";

const isSubCategory = (value: string) =>
  !isAnyOption(value) && value.includes("-");

const getDirectoryLabel = (label: string, isSubCategory: boolean) =>
  isSubCategory ? label.replace(/^\s*-\s*/, "") : label;

const DirectoryPanel = () => {
  const { t } = useTranslation();

  const directoryOptions = useAppSelector(selectDirectoryOptions);
  const customMarkers = useAppSelector(selectCustomMarkers);
  const lockedFilter = useAppSelector(selectLockedFilter);
  const activeValue = directoryOptions.value;

  const [resultsTotals, setResultsTotals] = useState<Record<string, number>>(
    {},
  );

  useEffect(() => {
    fetchResults(lockedFilter).then((results) => {
      if (results && !results.failed && results.resultsTotals)
        setResultsTotals(results.resultsTotals);
    });
  }, [lockedFilter]);

  return (
    <>
      <Heading title={t("directory")} />
      <StyledDirectoryPanel>
        <List>
          {directoryOptions.options.map((option, i) => {
            const subCategory = isSubCategory(option.value);
            const iconSrc = getDirectoryIconSrc({
              optionValue: option.value,
              customMarkers,
            });

            return (
              <DirectoryItem
                key={`${i}-${option.value}`}
                propId={directoryOptions.id}
                value={option.value}
                label={getDirectoryLabel(option.label, subCategory)}
                active={option.value === activeValue}
                resultsTotal={resultsTotals[option.value]}
                iconSrc={iconSrc}
                isSubCategory={subCategory}
              />
            );
          })}
        </List>
      </StyledDirectoryPanel>
    </>
  );
};

export default DirectoryPanel;
